import React, { useEffect, useState } from 'react';
import ShowValidationError from '../../../components/Common/ValidationError';
import Loader from '../../../components/Common/Loader';
import SubmitButton from '../../../components/Common/SubmitButton';
import CheckoutForm from '../../../components/Stripe/CheckoutForm';
import { UserModel } from '../../../interfaces/user.interface';
import { BookModel } from '../../../interfaces/book.interface';
import { GooglePlacesService } from '../../../services/googlePlaces.service';
import { postalCodeFormatMap } from '../../../config/countries';

interface ShippingInformationFormProps {
  isShare?: boolean;
  readonlyShippingAddress?: boolean;
  bookDetails: BookModel | null;
  user: UserModel | null;
  values: Record<string, string | number>;
  errors: Record<string, string> | any;
  handleChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  applyPromoCode: (coupon: string) => void;
  couponClass: string;
  couponLoader: boolean;
  setValues: React.Dispatch<React.SetStateAction<any>>;
  autocompletedCountry?: string | null;
  bookCost: number;
  netCost: number;
  shippingCost: number;
  bookTotalCost: number;
  discount: number | null;
  couponDescription: string | null;
  isBillingSameAsShipping: boolean;
  setIsBillingSameAsShipping: React.Dispatch<React.SetStateAction<boolean>>;
  singleBookPlan: boolean;
  usaStates: string[];
  countries: string[];
  paymentLoader: boolean;
  goBack: () => void;
}

const ShippingInformationForm: React.FC<ShippingInformationFormProps> = ({
  isShare,
  readonlyShippingAddress,
  bookDetails,
  user,
  values,
  errors,
  handleChange,
  handleSubmit,
  applyPromoCode,
  couponClass,
  couponLoader,
  setValues,
  autocompletedCountry,
  bookCost,
  netCost,
  shippingCost,
  bookTotalCost,
  discount,
  couponDescription,
  isBillingSameAsShipping,
  setIsBillingSameAsShipping,
  singleBookPlan,
  usaStates,
  countries,
  paymentLoader,
  goBack,
}: ShippingInformationFormProps) => {
  const [addressValidationErrors, setAddressValidationErrors] = useState<Record<string, string>>({});
  const [isValidatingAddress, setIsValidatingAddress] = useState<boolean>(false);
  const [isSubmitForm, setIsSubmitForm] = useState<boolean>(false);
  const [countryCode, setCountryCode] = useState<string>('');
  const [useSuggestion, setUseSuggestion] = useState<string>('');

  useEffect(() => {
    if (!autocompletedCountry) return;
    if (autocompletedCountry) {
      // Set the shipping country based on the autocompleted country
      setTimeout(() =>
        handleChange({
          target: { name: 'shippingCountry', value: autocompletedCountry },
        } as React.ChangeEvent<HTMLSelectElement>),
      );
    }
  }, [autocompletedCountry]);

  useEffect(() => {
    setCountryCode(getCountryCode(values.shippingCountry as string));
  }, [values.shippingCountry]);

  useEffect(() => {
    setUseSuggestion(isSubmitForm && !!(errors.shippingAddress1 || '') ? errors.shippingAddress1 : '');
  }, [values.shippingAddress1, isSubmitForm, errors.shippingAddress1?.error]);

  // Get country code for Google Places API
  const getCountryCode = (countryName: string): string => {
    return postalCodeFormatMap[countryName]?.code || 'us';
  };

  // Validate address using Google Places API
  const validateAddress = async (address: string) => {
    if (!address || address.length < 5) return;

    setIsValidatingAddress(true);
    try {
      const { shippingAddress2, shippingCity, shippingState, shippingZip, shippingCountry } = values;
      const countryCode = getCountryCode(shippingCountry as string);
      const fullAddress = [address, shippingAddress2, shippingCity, shippingState, shippingZip]
        .filter(Boolean)
        .map((item) => item.toString().trim())
        .filter(Boolean)
        .join(', ');
      const isValid = await GooglePlacesService.validateAddress(fullAddress, countryCode);

      if (!isValid) {
        setAddressValidationErrors((prev) => ({
          ...prev,
          shippingAddress1: 'Please enter a valid address',
        }));
      } else {
        setAddressValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.shippingAddress1;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('Error validating address:', error);
    } finally {
      setIsValidatingAddress(false);
    }
  };

  // Handle address blur for validation
  const handleAddressBlur = () => {
    if (values.shippingAddress1 && isSubmitForm) {
      validateAddress(values.shippingAddress1 as string);
    }
  };

  return (
    <div className="w-full flex flex-col items-start gap-4">
      <h1 className="mt-4 md:mt-0 text-storyblue text-xl ">Shipping Information</h1>

      {bookDetails?.isStudentBook && (
        <p className="text-secondaryStoryBlackText text-left">
          Your child's book will be printed with the whole class's orders and delivered to the school in one Class
          Shipment — no shipping address needed. The teacher hands out the books, ready for celebration day. 🎉
        </p>
      )}

      <form
        id="form-purchase"
        name="form-purchase"
        className={`${bookDetails?.isStudentBook ? 'mt-2' : 'mt-8'} text-start w-full flex flex-col items-start gap-2`}
        onSubmit={(data) => {
          handleSubmit(data);
          setIsSubmitForm(true);
        }}
      >
        <div className="w-full">
          <input
            type="text"
            name="shippingEmail"
            value={values.shippingEmail}
            onChange={handleChange}
            placeholder="Email"
            className={`p-2 w-full rounded-md ${!isShare ? 'text-[#9ea4b0]' : ''}`}
            readOnly={!isShare}
          />
          <ShowValidationError error={errors.shippingEmail!} />
        </div>
        {bookDetails && !bookDetails?.isStudentBook && (
          <>
            {!readonlyShippingAddress && <h1 className="text-[#a1a1a1] text-md  mt-6">Shipping Address</h1>}
            {readonlyShippingAddress && <h1 className="text-[#a1a1a1] text-md  mt-6">Company name</h1>}
            <div className="w-full">
              <input
                type="text"
                name="customerName"
                value={values.customerName}
                onChange={handleChange}
                placeholder="Name"
                className="p-2 w-full rounded-md"
                readOnly={readonlyShippingAddress}
              />
              <ShowValidationError error={errors.customerName!} />
            </div>
            {readonlyShippingAddress && (
              <>
                <h1 className="text-[#a1a1a1] text-md  mt-2">Recipient's Name</h1>
                <div className="w-full">
                  <input
                    type="text"
                    name="recipientName"
                    value={values.recipientName}
                    onChange={handleChange}
                    placeholder="Name"
                    className="p-2 w-full rounded-md"
                    readOnly={readonlyShippingAddress}
                  />
                  <ShowValidationError error={errors.customerName!} />
                </div>
                <h1 className="text-[#a1a1a1] text-md  mt-2">Shipping Address</h1>
              </>
            )}

            <div className="w-full">
              {/*{!readonlyShippingAddress ? (*/}
              {/*  <AddressAutocomplete*/}
              {/*    name="shippingAddress1"*/}
              {/*    value={values.shippingAddress1?.toString()}*/}
              {/*    country={countryCode}*/}
              {/*    onChange={(address: ParsedAddress | string) => {*/}
              {/*      if (typeof address === 'string') {*/}
              {/*        setValues((prevValues: any) => {*/}
              {/*          return {*/}
              {/*            ...prevValues,*/}
              {/*            shippingAddress1: address,*/}
              {/*          };*/}
              {/*        });*/}
              {/*        return;*/}
              {/*      }*/}
              {/*      setValues((prevValues: any) => {*/}
              {/*        console.log(address);*/}
              {/*        return {*/}
              {/*          ...prevValues,*/}
              {/*          shippingAddress1: address.formatted_address,*/}
              {/*        };*/}
              {/*      });*/}
              {/*    }}*/}
              {/*    placeholder="Street address with building number"*/}
              {/*    className="p-2 w-full rounded-md"*/}
              {/*    readOnly={readonlyShippingAddress}*/}
              {/*    useSuggestions={!!useSuggestion}*/}
              {/*  />*/}
              {/*) : (*/}
              <input
                type="text"
                name="shippingAddress1"
                value={values.shippingAddress1}
                onChange={handleChange}
                placeholder="Street address with building number"
                className="p-2 w-full rounded-md"
                readOnly={readonlyShippingAddress}
              />
              {/*)}*/}
              <ShowValidationError error={errors.shippingAddress1!} />
            </div>

            <div className="w-full">
              <input
                type="text"
                name="shippingAddress2"
                value={values.shippingAddress2}
                onChange={handleChange}
                placeholder="Address 2"
                className="p-2 w-full rounded-md"
                readOnly={readonlyShippingAddress}
              />
              <ShowValidationError
                error={errors.shippingAddress1 && values.shippingAddress2 ? errors.shippingAddress1 : ''}
              />
            </div>

            <div className="w-full mt-1">
              <select
                name="shippingCountry"
                onChange={handleChange}
                value={values.shippingCountry}
                className="p-2 w-full rounded-md"
                disabled={readonlyShippingAddress}
              >
                {countries.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <ShowValidationError error={errors.shippingCountry!} />
            </div>

            <div className="flex gap-7 mb-2">
              <div>
                <input
                  type="text"
                  name="shippingCity"
                  value={values.shippingCity}
                  onChange={handleChange}
                  placeholder="City"
                  className="p-2 w-full rounded-md"
                  readOnly={readonlyShippingAddress}
                  onBlur={handleAddressBlur}
                />
                <ShowValidationError error={errors.shippingCity! || addressValidationErrors.shippingCity} />
              </div>
              <div>
                {values.shippingCountry === 'United States' ? (
                  <select
                    name="shippingState"
                    value={values.shippingState}
                    onChange={handleChange}
                    className="p-[0.6rem] w-full rounded-md"
                    disabled={readonlyShippingAddress}
                  >
                    <option value="" disabled selected hidden>
                      State
                    </option>

                    {usaStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    name="shippingState"
                    value={values.shippingState}
                    onChange={handleChange}
                    placeholder={values.shippingCountry === 'United Kingdom' ? 'County' : 'State/Region'}
                    className="p-2 w-full rounded-md"
                    readOnly={readonlyShippingAddress}
                  />
                )}
                <ShowValidationError
                  error={
                    errors.shippingState
                      ? values.shippingCountry === 'United Kingdom'
                        ? errors.shippingState?.replace('State', 'County')
                        : values.shippingCountry !== 'United States'
                          ? errors.shippingState?.replace('State', 'State/Region')
                          : errors.shippingState
                      : ''
                  }
                />
              </div>

              <div>
                <input
                  type="text"
                  name="shippingZip"
                  value={values.shippingZip}
                  onChange={handleChange}
                  placeholder="Zip"
                  className="p-2 w-full rounded-md"
                  readOnly={readonlyShippingAddress}
                />
                <ShowValidationError error={errors.shippingZip! || addressValidationErrors.shippingZip} />
              </div>
            </div>
          </>
        )}
        <div className="w-full flex flex-col items-start gap-2">
          {bookCost && netCost && shippingCost && bookTotalCost ? (
            <div
              className={`${bookCost && netCost && shippingCost && bookTotalCost ? 'flex' : 'hidden'} w-full flex-col items-start gap-2`}
            >
              <h1 className="text-[#a1a1a1] text-md mt-6">Card Information</h1>
              <div className="w-full">
                <input
                  type="text"
                  name="cardHolderName"
                  value={values.cardHolderName}
                  onChange={handleChange}
                  placeholder="Cardholder Name"
                  className="p-2 w-full rounded-md"
                />
                <ShowValidationError error={errors.cardHolderName!} />
              </div>
              <div className="w-full bg-white p-2 h-9 rounded-md">
                <CheckoutForm />
              </div>
            </div>
          ) : null}
          <div className="w-full p-2  md:mt-2 rounded-md">
            <div className="w-full flex justify-between gap-2 flex-wrap md:flex-nowrap">
              {bookCost && netCost && shippingCost && bookTotalCost ? (
                <div className="w-full">
                  <h1 className="text-[#a1a1a1] text-md mt-2">Coupon Code</h1>
                  <div className="flex justify-between items-center gap-2 mt-2">
                    <input
                      type="text"
                      name="coupon"
                      value={values.coupon}
                      onChange={(e) => {
                        setValues((prevValues: any) => ({
                          ...prevValues,
                          coupon: e.target.value,
                        }));
                      }}
                      placeholder="PROMO CODE (OPTIONAL)"
                      className={`w-full p-2 rounded-md uppercase ${couponClass} `}
                    />

                    <button
                      type="button"
                      className="w-79 flex justify-center items-center whitespace-nowrap gap-1 bg-storyblueSecondLightText text-white font-bold rounded-2xl px-4 py-2 text-sm md:text-md"
                      onClick={() => applyPromoCode(values.coupon?.toString())}
                    >
                      Apply Coupon / Gift Card {couponLoader && <Loader loader={couponLoader} />}
                    </button>
                  </div>
                </div>
              ) : null}
              <div className="w-[30%]">
                <h1 className="text-[#a1a1a1] text-md mt-2">Book Quantity</h1>
                <input
                  type="number"
                  name="qty"
                  min={1}
                  value={values.qty}
                  readOnly={singleBookPlan}
                  onChange={handleChange}
                  placeholder="Quantity"
                  className="mt-2 w-full p-2 rounded-md"
                  max="100"
                  required
                  pattern="[1-9][0-9]*"
                />
              </div>
            </div>
            {discount && couponDescription?.length! > 0 && (
              <div className="flex pt-2">
                <span className=" text-storygreen text-sm">
                  <strong> Description: </strong> {couponDescription}
                </span>
              </div>
            )}
            {!bookDetails?.isStudentBook && bookCost && netCost && shippingCost && bookTotalCost ? (
              <div className="flex items-baseline mt-8">
                <input
                  type="checkbox"
                  name="terms"
                  checked={isBillingSameAsShipping}
                  onChange={(e) => setIsBillingSameAsShipping(e.target.checked)}
                  className="mr-2 rounded-md"
                />
                <label className="flex text-start">Billing info is same as shipping</label>
              </div>
            ) : (
              ''
            )}
            {!bookDetails?.isStudentBook && !isBillingSameAsShipping && (
              <div className="mt-8 text-start w-full flex flex-col items-start gap-2">
                <h1 className="text-[#a1a1a1] text-md font-bold mt-6">Billing Address</h1>
                <input
                  type="text"
                  name="billingCardHolderName"
                  value={values.billingCardHolderName}
                  onChange={handleChange}
                  placeholder="Name"
                  className="p-2 w-full rounded-md"
                />
                <ShowValidationError error={errors.billingCardHolderName!} />
                <input
                  type="text"
                  name="billingAddress1"
                  value={values.billingAddress1}
                  onChange={handleChange}
                  placeholder="Address 1"
                  className="p-2 w-full rounded-md"
                />
                <ShowValidationError error={errors.billingAddress1!} />
                <input
                  type="text"
                  name="billingAddress2"
                  value={values.billingAddress2}
                  onChange={handleChange}
                  placeholder="Address 2"
                  className="p-2 w-full rounded-md"
                />

                <div className="flex gap-6 mt-1">
                  <div>
                    <input
                      type="text"
                      name="billingCity"
                      value={values.billingCity}
                      onChange={handleChange}
                      placeholder="City"
                      className="p-2 w-full rounded-md"
                    />
                    <ShowValidationError error={errors.billingCity!} />
                  </div>
                  <div>
                    <select
                      name="billingState"
                      value={values.billingState}
                      onChange={handleChange}
                      className="p-2 w-full rounded-md"
                    >
                      <option value=""></option>
                      {usaStates.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    <ShowValidationError error={errors.billingState!} />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="billingPostalCode"
                      value={values.billingPostalCode}
                      onChange={handleChange}
                      placeholder="Zip"
                      className="p-2 w-full rounded-md"
                    />
                    <ShowValidationError error={errors.billingPostalCode!} />
                  </div>
                </div>
              </div>
            )}
            <div className={`${bookDetails?.isStudentBook ? 'mt-2' : ''}`}>
              <SubmitButton type="submit" title="Purchase" loader={paymentLoader} cancelOperation={goBack} />
              {/*<p className="text-base mt-2 italic">*/}
              {/*  Please note that the shipping and handling cost is <strong>${shippingCost}</strong>, and all packages*/}
              {/*  are shipped via the <strong>United States Postal Service (USPS)</strong> for timely and secure delivery.*/}
              {/*</p>*/}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ShippingInformationForm;
